import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthProvider';
import { CheckCircle, XCircle, Loader, Mail, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

export const TeamInviteAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'checking' | 'register' | 'login' | 'accepting' | 'success' | 'error'>('checking');
  const [message, setMessage] = useState('');
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [emailMismatchError, setEmailMismatchError] = useState<{
    invitationEmail: string;
    userEmail: string;
  } | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Ungültiger Einladungslink - Token fehlt');
      return;
    }

    // Check invitation details first
    checkInvitation();
  }, [token]);

  useEffect(() => {
    // If user is authenticated and we have invitation details, accept it
    if (session?.access_token && invitationDetails && invitationDetails.status === 'pending' && 
        (status === 'checking' || status === 'login' || status === 'register') && !isAccepting) {
      console.log('🔐 [INVITE] User authenticated with pending invitation, accepting automatically');
      acceptInvitation();
    }
  }, [session?.access_token, invitationDetails, status, isAccepting]);

  const checkInvitation = async () => {
    try {
      setStatus('checking');
      setMessage('Einladung wird überprüft...');

      console.log('🔍 [INVITE] Checking invitation with token:', token);
      console.log('🔍 [INVITE] Current auth state:', {
        hasSession: !!session?.access_token,
        authLoading,
        userEmail: session?.user?.email
      });

      // First, check if the invitation is valid by making an unauthenticated request
      const response = await fetch(`${API_BASE}/api/team/invitation-details?token=${token}`);
      
      console.log('🔍 [INVITE] Invitation check response:', {
        status: response.status,
        ok: response.ok
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 [INVITE] Invitation details:', data);
        
        // Check if invitation is already accepted
        if (data.status === 'already_accepted') {
          console.log('✅ [INVITE] Invitation already accepted, redirecting to dashboard');
          setStatus('success');
          setMessage('Diese Einladung wurde bereits angenommen. Sie werden zum Dashboard weitergeleitet...');
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/qr/dash', { replace: true });
          }, 2000);
          return;
        }
        
        setInvitationDetails(data);
        setEmail(data.email);
        
        // If user is not logged in, show register/login options
        if (!session?.access_token) {
          console.log('🔍 [INVITE] User not logged in, showing register/login options');
          setStatus('register');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('🔍 [INVITE] Invalid invitation:', errorData);
        setStatus('error');
        setMessage('Ungültige oder abgelaufene Einladung');
      }
    } catch (error) {
      console.error('🔍 [INVITE] Error checking invitation:', error);
      setStatus('error');
      setMessage('Fehler beim Überprüfen der Einladung');
    }
  };

  const handleRegisterOrLogin = async () => {
    if (!email) return;

    try {
      setIsRegistering(true);
      setMessage('Magic Link wird gesendet...');

      // Send magic link
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/qr/team/accept-invite?token=${token}&from_magic_link=true`,
          data: {
            invitation_token: token,
            redirect_to: `/qr/team/accept-invite?token=${token}&from_magic_link=true`
          }
        }
      });

      if (error) {
        throw error;
      }

      setStatus('login');
      setMessage(`Ein Magic Link wurde an ${email} gesendet. Bitte überprüfen Sie Ihre E-Mail.`);
    } catch (error) {
      console.error('Error sending magic link:', error);
      setMessage('Fehler beim Senden des Magic Links');
    } finally {
      setIsRegistering(false);
    }
  };

  const acceptInvitation = async () => {
    if (!session?.access_token || !token || isAccepting) {
      console.log('🔐 [INVITE] Cannot accept invitation - missing session, token, or already accepting:', {
        hasSession: !!session?.access_token,
        hasToken: !!token,
        isAccepting
      });
      return;
    }

    try {
      setIsAccepting(true);
      setStatus('accepting');
      setMessage('Einladung wird angenommen...');

      console.log('🔐 [INVITE] Accepting invitation with token:', token);

      const response = await fetch(`${API_BASE}/api/team/accept-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ token })
      });

      console.log('🔐 [INVITE] Accept invitation response:', {
        status: response.status,
        ok: response.ok
      });

      if (response.ok) {
        const data = await response.json();
        setStatus('success');
        setMessage('Erfolgreich dem Team beigetreten!');
        
        console.log('🔐 [INVITE] Invitation accepted successfully, redirecting to dashboard...');
        
        // Clear URL parameters and redirect to dashboard immediately
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          console.log('🔐 [INVITE] Redirecting to dashboard...');
          navigate('/qr/dash', { replace: true });
        }, 1500);
      } else {
        const errorData = await response.json();
        console.error('🔐 [INVITE] Error accepting invitation:', errorData);
        
        setStatus('error');
        
        // Handle specific error cases
        if (errorData.error === 'Invitation email does not match your account' && errorData.details) {
          setEmailMismatchError({
            invitationEmail: errorData.details.invitationEmail,
            userEmail: errorData.details.yourEmail
          });
          setMessage(
            `Diese Einladung wurde an ${errorData.details.invitationEmail} gesendet. Bitte melden Sie sich mit dieser E-Mail-Adresse an, um die Einladung anzunehmen.`
          );
        } else {
          setMessage(errorData.error || 'Fehler beim Beitreten zum Team');
        }
      }
    } catch (error) {
      console.error('🔐 [INVITE] Error accepting invitation:', error);
      setStatus('error');
      setMessage('Netzwerkfehler beim Verarbeiten der Einladung');
    } finally {
      setIsAccepting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
            <UserPlus className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Team-Einladung
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sie wurden eingeladen, einem Team bei BeyondPages beizutreten
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          {status === 'checking' && (
            <div className="text-center space-y-4">
              <Loader className="h-12 w-12 text-blue-500 animate-spin mx-auto" />
              <p className="text-gray-600">{message}</p>
            </div>
          )}

          {status === 'register' && invitationDetails && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-gray-700">
                  Sie wurden von <strong>{invitationDetails.inviter}</strong> eingeladen,
                  dem Team als <strong>{invitationDetails.role === 'admin' ? 'Administrator' : 'Mitglied'}</strong> beizutreten.
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Ihre E-Mail-Adresse:</strong> {email}
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Um fortzufahren, senden wir Ihnen einen Magic Link an Ihre E-Mail-Adresse.
                </p>
                
                <button
                  onClick={handleRegisterOrLogin}
                  disabled={isRegistering}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isRegistering ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Magic Link senden
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {status === 'login' && (
            <div className="text-center space-y-4">
              <Mail className="h-12 w-12 text-blue-500 mx-auto" />
              <div>
                <p className="text-gray-700 font-medium">Magic Link gesendet!</p>
                <p className="text-sm text-gray-600 mt-2">{message}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p>Nach der Anmeldung werden Sie automatisch dem Team hinzugefügt.</p>
              </div>
            </div>
          )}

          {status === 'accepting' && (
            <div className="text-center space-y-4">
              <Loader className="h-12 w-12 text-blue-500 animate-spin mx-auto" />
              <p className="text-gray-600">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
              <p className="text-green-600 font-medium">{message}</p>
              <p className="text-sm text-gray-500">
                Sie werden automatisch zum Dashboard weitergeleitet...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-4">
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              <p className="text-red-600 font-medium">{message}</p>
              
              {emailMismatchError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                  <h4 className="font-medium text-yellow-800 mb-2">Falsche E-Mail-Adresse</h4>
                  <div className="text-sm text-yellow-700 space-y-2">
                    <p><strong>Einladung gesendet an:</strong> {emailMismatchError.invitationEmail}</p>
                    <p><strong>Sie sind angemeldet als:</strong> {emailMismatchError.userEmail}</p>
                    <p className="mt-2">Bitte melden Sie sich mit der richtigen E-Mail-Adresse an.</p>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={async () => {
                        try {
                          await supabase.auth.signOut();
                        } catch {}
                        const redirect = `/qr/team/accept-invite?token=${token}`;
                        const email = emailMismatchError.invitationEmail;
                        // Prefill email and carry redirect target to login so magic link returns here
                        navigate(`/qr/login?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirect)}`);
                      }}
                      className="w-full bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                    >
                      Abmelden und mit {emailMismatchError.invitationEmail} anmelden
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/qr/login')}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Zur Anmeldung
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Zur Startseite
                </button>
              </div>
            </div>
          )}
        </div>

        {user && status !== 'success' && status !== 'error' && (
          <div className="text-center text-sm text-gray-500">
            Angemeldet als: {user.email}
          </div>
        )}
      </div>
    </div>
  );
}; 
